import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import { clickFromKeyboard } from '@/util/keyboardUtil';
import { useDashboardAnalytics } from '@/query/analytics';
import { useLocale, useScenarioId } from '@/hook';
import Button from '@/component/Button';
import Tooltip from '@/component/Tooltip';
import { DateRangePicker } from '@/component/dateRangePicker';
import { SpeechBubbleIcon } from '@/assets/svgs/icons';
import MessageIcon from '@/assets/svgs/message.svg';
import PeopleIcon from '@/assets/svgs/people.svg';
import EllipsisBubbleIcon from '@/assets/svgs/ellipsis_bubble.svg';

import DailyLineGraph from './component/DailyLineGraph';
import WeekyHourlyTable from './component/WeekyHourlyTable';
import FeedbackCardBody from './component/FeedbackCardBody';
import IntentCardBody from './component/IntentCardBody';


type SummaryType = "user" | "message" | "chat" | "messagePerChat";

const SUMMARY_lABELS_KEYS = {
    user: "i01004",
    message: "b0056",
    chat: "i01005",
    messagePerChat: "i01006",
};

const WEEKLY_HOURLY_COLOR_CLASSES = ['bg-chat-fifth', 'bg-chat-fourth', 'bg-chat-third', 'bg-chat-second', 'bg-chat-first'];
const WEEKLY_HOURLY_BREAK_POINTS = [20, 40, 60, 80];

const isSameDateRange = (dateRange1: { start: Moment, end: Moment }, dateRange2: { start: Moment, end: Moment }) => {
    return dateRange1.start.isSame(dateRange2.start, 'day') && dateRange1.end.isSame(dateRange2.end, 'day');
};

const DashboardView: React.FC = () => {
    const intl = useIntl();
    const { locale } = useLocale();

    const scenarioId = useScenarioId();
    const today = useMemo(() => moment().endOf('day'), []);
    const baseDate = useMemo(() => today.clone().startOf('day').add(1, 'day'), [today]);
    const oneWeekAgo = useMemo(() => baseDate.clone().subtract(7, 'days'), [baseDate]);
    const oneMonthAgo = useMemo(() => baseDate.clone().subtract(1, 'month'), [baseDate]);
    const threeMonthsAgo = useMemo(() => baseDate.clone().subtract(3, 'months'), [baseDate]);

    const [period, setPeriod] = useState<{ start: Moment, end: Moment }>({ start: threeMonthsAgo, end: moment().endOf('day') });
    const [graphType, setGraphType] = useState<SummaryType>('user');

    const { data: analytics } = useDashboardAnalytics(scenarioId, period?.start.toISOString(), period?.end.toISOString());

    const effectiveMessageCount = analytics ? analytics.userMessageCount - analytics.fallbackMessageCount : 0;
    const effectiveMessagePerChat = analytics?.effectiveConversationCount ? effectiveMessageCount / analytics.effectiveConversationCount : 0;

    return (
        <main className="scenario-content-layout flex flex-col gap-4">
            <div className="w-full min-w-md-page flex items-center gap-1.5">
                <div className="flex items-center gap-1.5">
                    <DateRangePicker max={90}
                                     period={period}
                                     onChangePeriod={setPeriod}
                    >
                        <Button size="md"
                                className="date-range-picker-btn w-[172px] flex items-center justify-between"
                        >
                            <div>{period.start.clone().format("YYYY.MM.DD")}</div>
                            <div>~</div>
                            <div>{period.end.clone().format("YYYY.MM.DD")}</div>
                        </Button>
                    </DateRangePicker>
                    <Button size="md"
                            className={classNames(
                                "date-range-picker-btn",
                                locale === "en" ? "w-[90px]" : "w-[72px]",
                                isSameDateRange(period, { start: oneWeekAgo, end: today }) && "active"
                            )}
                            aria-pressed={isSameDateRange(period, { start: oneWeekAgo, end: today })}
                            onClick={() => setPeriod({ start: oneWeekAgo, end: today })}
                    >
                        {intl.formatMessage({ id: "i01001" })}
                    </Button>
                    <Button size="md"
                            className={classNames(
                                "date-range-picker-btn",
                                locale === "en" ? "w-[90px]" : "w-[72px]",
                                isSameDateRange(period, { start: oneMonthAgo, end: today }) && "active"
                            )}
                            aria-pressed={isSameDateRange(period, { start: oneMonthAgo, end: today })}
                            onClick={() => setPeriod({ start: oneMonthAgo, end: today })}
                    >
                        {intl.formatMessage({ id: "i01002" })}
                    </Button>
                    <Button size="md"
                            className={classNames(
                                "date-range-picker-btn",
                                locale === "en" ? "w-[90px]" : "w-[72px]",
                                isSameDateRange(period, { start: threeMonthsAgo, end: today }) && "active"
                            )}
                            aria-pressed={isSameDateRange(period, { start: threeMonthsAgo, end: today })}
                            onClick={() => setPeriod({ start: threeMonthsAgo, end: today })}
                    >
                        {intl.formatMessage({ id: "i01003" })}
                    </Button>
                </div>
            </div>

            {analytics && (<>
                <div className="w-full min-w-md-page flex-none grid grid-cols-[repeat(4,1fr)] items-center gap-4.5">
                    <SummaryCard title={intl.formatMessage({ id: SUMMARY_lABELS_KEYS.user })}
                                 icon={<img src={PeopleIcon} alt="" className="size-4" />}
                                 value={analytics.messagingUserCount.toLocaleString()}
                                 isSelected={graphType === "user"}
                                 onClick={() => setGraphType("user")}
                    />
                    <SummaryCard title={intl.formatMessage({ id: SUMMARY_lABELS_KEYS.message })}
                                 icon={<img src={MessageIcon} alt="" className="size-4" />}
                                 value={effectiveMessageCount.toLocaleString()}
                                 isSelected={graphType === "message"}
                                 onClick={() => setGraphType("message")}
                    />
                    <SummaryCard title={intl.formatMessage({ id: SUMMARY_lABELS_KEYS.chat })}
                                 icon={<SpeechBubbleIcon className="size-4" />}
                                 value={analytics.effectiveConversationCount.toLocaleString()}
                                 isSelected={graphType === "chat"}
                                 onClick={() => setGraphType("chat")}
                    />
                    <SummaryCard title={intl.formatMessage({ id: SUMMARY_lABELS_KEYS.messagePerChat })}
                                 icon={<img src={EllipsisBubbleIcon} alt="" className="size-4" />}
                                 value={effectiveMessagePerChat.toFixed(1)}
                                 isSelected={graphType === "messagePerChat"}
                                 onClick={() => setGraphType("messagePerChat")}
                    />
                </div>

                <div className="relative -mt-1 w-full min-w-md-page h-[350px] rounded-[6px] border border-black-200">
                    <div className="relative top-1 size-full px-8 pb-6 pt-5 flex flex-col gap-6 border border-black-200 rounded-[6px] bg-white z-10">
                        <div className="text-[15px] leading-none">
                            <span className="text-chat42"
                                  aria-hidden="true"
                            >
                                {"●"}
                            </span>
                            <span className="ml-1 font-[600] text-black-500">    
                                {`${intl.formatMessage({ id: SUMMARY_lABELS_KEYS[graphType] })} ${intl.formatMessage({ id: "i01008" })}`}
                            </span>
                        </div>
                        <DailyLineGraph height={260}
                                        type={graphType}
                                        tooltipLabel={intl.formatMessage({ id: SUMMARY_lABELS_KEYS[graphType] })}
                                        counts={analytics.dailyUsageCount}
                        />
                    </div>

                    <div className="absolute top-0 left-0 right-0 size-full rounded-[6px] bg-chat42 z-0" />
                </div>

                <div className="w-full min-w-md-page mt-1 h-[400px] grid grid-cols-[repeat(4,1fr)] gap-x-4 break-keep">
                    <div className="col-span-2 px-8 py-6 flex flex-col gap-5 border border-black-200 rounded-[6px] bg-white">
                        <div className="w-full flex items-center">
                            <div className="text-[15px] font-[600] leading-none text-black-500">
                                {intl.formatMessage({ id: "i01009" })}
                            </div>
                            <div className="ml-1">
                                <Tooltip placement="right"
                                         message={intl.formatMessage({ id: "i01010" })}
                                />
                            </div>
                            <div className="ml-auto flex items-center gap-2">
                                <div className="text-[12px] font-[400] leading-none text-black-500">
                                    {intl.formatMessage({ id: "b0057" })}
                                </div>
                                <div className="flex items-center gap-px">
                                    {WEEKLY_HOURLY_COLOR_CLASSES.map((className, index) => (
                                        <Tooltip key={className}
                                                 placement="top"
                                                 offset={2}
                                                 message={intl.formatMessage({ id: `i01019_${index + 1}` })}
                                        >
                                            <div className={classNames("w-5 h-2.5", className)} />
                                        </Tooltip>
                                    ))}
                                </div>
                                <div className="text-[12px] font-[400] leading-none text-black-500">
                                    {intl.formatMessage({ id: "b0058" })}
                                </div>
                            </div>
                        </div>
                        
                        <WeekyHourlyTable period={period}
                                          breakPoints={WEEKLY_HOURLY_BREAK_POINTS}
                                          emptyColorClass="bg-chat-empty"
                                          colorClasses={WEEKLY_HOURLY_COLOR_CLASSES}
                                          counts={analytics.weekHourlyMessageCount}
                        />
                    </div>

                    <div className="px-8 py-6 flex flex-col gap-6 border border-black-200 rounded-[6px] bg-white">
                        <div className="text-[15px] font-[600] leading-none text-black-500">
                            {intl.formatMessage({ id: "i01011" })}
                        </div>

                        <IntentCardBody ranking={analytics.intentRanking}
                                        total={analytics.userMessageCount}
                        />
                    </div>

                    <div className="px-5 py-6 flex flex-col gap-6 border border-black-200 rounded-[6px] bg-white">
                        <div className="flex items-end gap-1.5">
                            <div className="text-[15px] font-[600] leading-[1.2] text-black-500">
                                {intl.formatMessage({ id: "i01013" })}
                            </div>
                            <div className="text-[12px] font-[400] leading-[1.2] text-black-500">
                                {`(${intl.formatMessage({ id: "i01014" })})`}
                            </div>
                        </div>

                        <FeedbackCardBody counts={analytics.feedback}
                                          total={analytics.userMessageCount}
                        />
                    </div>
                </div>
            </>)}
        </main>
    );
};

interface SummaryCardProps {
    title: string;
    icon: React.ReactNode;
    value: string;
    isSelected: boolean;
    onClick: () => void;
}

const SummaryCard: React.FC<SummaryCardProps> = (props) => {
    const { title, icon, value, isSelected, onClick } = props;

    return (
        <div className="relative w-full h-[136px] cursor-pointer"
             role="button"
             tabIndex={0}
             aria-pressed={isSelected}
             aria-label={title}
             onClick={onClick}
             onKeyDown={clickFromKeyboard}
        >
            <div className="relative w-full h-[calc(100%-4px)] flex flex-col justify-start items-start gap-4 rounded-[14px] border border-black-200 bg-white z-10 hover:bg-black-100">
                <div className="w-full h-[60px] px-6 flex items-start justify-between">
                    <div className="mt-5 text-[16px] font-[500] leading-none text-black-500">
                        {title}
                    </div>
                    <div className="mt-4 ml-2 size-8 flex items-center justify-center rounded-full bg-chat42-10">
                        {icon}
                    </div>
                </div>
                <div className={classNames(
                         "ml-0.5 w-full h-full px-6 flex flex-col justify-start items-start",
                         isSelected ? "text-chat42" : "text-black-500"
                     )}
                >
                    <div className="text-[30px] font-[700] leading-[1.2]">
                        {value}
                    </div>
                </div>
            </div>

            {isSelected && (
                <div className="absolute top-0 w-full h-full bg-chat42 rounded-[18px] shadow-xl z-0" />
            )}
        </div>
    );
};

export default DashboardView;
